import * as ImagePicker from 'expo-image-picker'
import { useState } from 'react'
import { Linking } from 'react-native'
import { errorToString } from '@/lib/errors'
import { deleteOrgLogo, uploadOrgLogo } from '@/lib/file-upload'
import { log } from '@/lib/logger'
import { notify } from '@/lib/notice'

const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp']

function validateImage(asset: ImagePicker.ImagePickerAsset): string | null {
    const mimeType = asset.mimeType || 'image/png'

    if (!ALLOWED_TYPES.includes(mimeType)) {
        return 'Please select a JPEG, PNG, SVG, or WebP image'
    }

    if (asset.fileSize && asset.fileSize > MAX_FILE_SIZE) {
        return 'Image must be less than 2MB'
    }

    return null
}

async function ensurePermission(
    permission: ImagePicker.PermissionResponse | null,
    requestPermission: () => Promise<ImagePicker.PermissionResponse>
): Promise<boolean> {
    if (permission?.granted) return true

    const result = await requestPermission()
    if (result.granted) return true

    if (!result.canAskAgain) {
        notify.error('Please enable photo access in Settings')
        Linking.openSettings()
    } else {
        notify.error('Permission to access photos is required')
    }
    return false
}

interface UseOrgLogoResult {
    isUploading: boolean
    isDeleting: boolean
    pickAndUploadLogo: () => Promise<void>
    removeLogo: () => Promise<void>
}

export function useOrgLogo(orgId: string | undefined): UseOrgLogoResult {
    const [isUploading, setIsUploading] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [permission, requestPermission] = ImagePicker.useMediaLibraryPermissions()

    const pickAndUploadLogo = async () => {
        if (!orgId) return

        const hasPermission = await ensurePermission(permission, requestPermission)
        if (!hasPermission) return

        const result = await ImagePicker.launchImageLibraryAsync({
            mediaTypes: ['images'],
            allowsEditing: true,
            aspect: [10, 3],
            quality: 0.8,
        })

        if (result.canceled || !result.assets[0]) return

        const asset = result.assets[0]
        const validationError = validateImage(asset)
        if (validationError) {
            notify.error(validationError)
            return
        }

        setIsUploading(true)
        try {
            await uploadOrgLogo(orgId, asset.uri, asset.mimeType || 'image/png')
            notify.success('Logo uploaded successfully')
        } catch (error) {
            log.errorWithoutSentry('Failed to upload logo:', errorToString(error))
            notify.error('Failed to upload logo')
        } finally {
            setIsUploading(false)
        }
    }

    const removeLogo = async () => {
        if (!orgId) return

        setIsDeleting(true)
        try {
            await deleteOrgLogo(orgId)
            notify.success('Logo removed')
        } catch (error) {
            log.errorWithoutSentry('Failed to remove logo:', errorToString(error))
            notify.error('Failed to remove logo')
        } finally {
            setIsDeleting(false)
        }
    }

    return {
        isUploading,
        isDeleting,
        pickAndUploadLogo,
        removeLogo,
    }
}
